import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, Download, Upload, Tags, Wallet, ShoppingCart, RotateCcw, History } from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { KpiCard } from '@/components/ui/KpiCard';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { Table } from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { SearchableSelect, type SearchableOption } from '@/components/form/SearchableSelect';
import { ExcelImportModal } from '@/components/ui/ExcelImportModal';
import {
  listSisSales,
  listSisStores,
  importSisSalesReport,
  importSisGroupReport,
  sisSalesReportTemplatePath,
  type SisSaleRow,
  type OversoldLine,
} from '@/api/sisStock';
import { usePrompt } from '@/components/ui/usePrompt';
import { StoreLabelsModal } from '../sis-stock/StoreLabelsModal';
import { SalesUploadsModal } from './SalesUploadsModal';
import { downloadExcel } from '@/lib/downloadExcel';
import { formatINR, formatDate } from '@/lib/format';

const UNGROUPED = 'Ungrouped';

/**
 * Tax is stored as a magnitude while a return's gross (netValue) is negative —
 * the minus only marks the return. Give tax the line's sign so Net = Gross − Tax
 * stays right for returns (−5,354.70 − (−254.98) = −5,099.72, not −5,609.68).
 */
const signedTax = (r: { netValue: number; taxAmount: number }) => (r.netValue < 0 ? -Math.abs(r.taxAmount) : r.taxAmount);

export function SisSalesPage() {
  const qc = useQueryClient();
  const ask = usePrompt();

  // The server stops an upload whose lines would sell more than a store holds
  // (usually a wrong barcode at the store's billing) and lists them; nothing is
  // saved. Show them and upload only if the user confirms.
  async function uploadSold(run: (allowOversold: boolean) => ReturnType<typeof importSisSalesReport>) {
    try {
      return await run(false);
    } catch (e) {
      const data = (e as { response?: { data?: { code?: string; oversold?: OversoldLine[] } } }).response?.data;
      if (data?.code !== 'OVERSOLD' || !data.oversold?.length) throw e;
      const lines = data.oversold;
      const shown = lines
        .slice(0, 12)
        .map((o) => `• ${uploadTarget === 'group' ? `${o.store}: ` : ''}${o.itemCode} (${o.barcode}) · bill ${o.billNo} — on hand ${o.onHand}, sold ${o.qty} → ${o.after}`)
        .join('\n');
      const more = lines.length > 12 ? `\n…and ${lines.length - 12} more` : '';
      const ok = await ask({
        title: `${lines.length} line${lines.length === 1 ? '' : 's'} would take stock below zero`,
        message: `The store reports selling more than the ERP sent it — usually a wrong barcode at the store's billing. Nothing has been saved yet.\n\n${shown}${more}\n\nCheck these with the store first. If you upload anyway, they are also listed in SIS Exceptions.`,
        input: false,
        tone: 'danger',
        wide: true,
        confirmLabel: 'Upload anyway',
      });
      if (ok === null) throw Object.assign(new Error('Upload cancelled — nothing was saved.'), { name: 'UserCancelled' });
      return run(true);
    }
  }
  const [group, setGroup] = useState('');
  const [partyId, setPartyId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [search, setSearch] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const [labelsOpen, setLabelsOpen] = useState(false);
  const [uploadsOpen, setUploadsOpen] = useState(false);

  const filter = useMemo(
    () => ({
      group: group || undefined,
      partyId: partyId ? Number(partyId) : undefined,
      from: from || undefined,
      to: to || undefined,
    }),
    [group, partyId, from, to],
  );

  const storesQ = useQuery({ queryKey: ['sis-stores'], queryFn: () => listSisStores(), staleTime: 0 });
  const query = useQuery({
    queryKey: ['sis-sales', filter],
    queryFn: () => listSisSales(filter),
    refetchInterval: 60_000,
  });
  const rows = query.data ?? [];
  const stores = storesQ.data ?? [];
  const selectedStore = stores.find((s) => String(s.id) === partyId);
  // A specific store → single upload; else a chosen group → group-wise upload.
  const uploadTarget: 'store' | 'group' | null = selectedStore ? 'store' : group ? 'group' : null;

  const groupOptions = useMemo(
    () => Array.from(new Set(stores.map((s) => s.group || UNGROUPED))).sort(),
    [stores],
  );
  const storeOptions = useMemo<SearchableOption[]>(
    () => [
      { value: '', label: 'All Stores' },
      ...stores
        .filter((s) => !group || (s.group || UNGROUPED) === group)
        .map((s) => ({ value: String(s.id), label: s.name, sublabel: s.city || undefined })),
    ],
    [stores, group],
  );

  const kpis = useMemo(() => {
    let gross = 0, tax = 0, units = 0, returns = 0;
    for (const r of rows) {
      gross += r.netValue;
      tax += signedTax(r);
      if (r.isReturn) returns += r.qty;
      else units += r.qty;
    }
    return { gross, netExGst: gross - tax, units, returns };
  }, [rows]);

  return (
    <div>
      <SectionHeader
        title="SIS Sales Report"      />

      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <KpiCard label="Gross Sales" value={`₹${formatINR(kpis.gross)}`} accent="#6366f1" Icon={Wallet} />
        <KpiCard label="Net Sales (ex-GST)" value={`₹${formatINR(kpis.netExGst)}`} accent="#8b5cf6" Icon={Wallet} />
        <KpiCard label="Units Sold" value={kpis.units} accent="#10b981" Icon={ShoppingCart} />
        <KpiCard label="Returns (units)" value={kpis.returns} accent="#f59e0b" Icon={RotateCcw} />
      </div>

      <FilterBar>
        {/* No "Search" label here — the icon and placeholder say it, and the room goes to the Store filter. */}
        <div className="relative col-span-2 min-w-0 sm:col-auto sm:w-[170px]">
          <Search size={11} className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] sm:left-2.5" strokeWidth={2.25} />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search" title="Search by bill no, item or barcode" className="!pl-5 sm:!pl-8" aria-label="Search sales" />
        </div>
        <FilterField label="Group" className="sm:w-[195px]">
          <Select
            value={group}
            onChange={(e) => { setGroup(e.target.value); setPartyId(''); }}
          >
            <option value="">All Groups</option>
            {groupOptions.map((g) => <option key={g}>{g}</option>)}
          </Select>
        </FilterField>
        <FilterField label="Store" className="col-span-2 sm:col-auto sm:w-[240px]">
          <SearchableSelect options={storeOptions} value={partyId} onChange={(v) => setPartyId(v === '' ? '' : String(v))} placeholder="Type a store name…" />
        </FilterField>
        <FilterField label="From" className="sm:w-[162px]">
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} aria-label="From date" />
        </FilterField>
        <FilterField label="To" className="sm:w-[146px]">
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} aria-label="To date" />
        </FilterField>
        <div className="col-span-2 flex flex-wrap items-end gap-1.5 sm:col-span-1 sm:ml-auto sm:self-end">
          <Button variant="glass" size="sm" onClick={() => setUploadsOpen(true)} title="Review recent sales-report uploads and delete a wrong one">
            <span className="inline-flex items-center gap-1"><History size={12} /> Uploads</span>
          </Button>
          <Button variant="glass" size="sm" onClick={() => setLabelsOpen(true)} title="Configure each store's report label for group-wise uploads">
            <span className="inline-flex items-center gap-1"><Tags size={12} /> Labels</span>
          </Button>
          <Button
            variant="glass"
            size="sm"
            onClick={() => downloadExcel(sisSalesReportTemplatePath('sold', uploadTarget === 'group'), `GRHAMOY_SIS_SalesReport${uploadTarget === 'group' ? '_GroupWise' : ''}_Template.xlsx`)}
            title={uploadTarget === 'group' ? 'Group-wise template (with Store column)' : 'Sales-report template'}
          >
            <span className="inline-flex items-center gap-1"><Download size={12} /> Template</span>
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={!uploadTarget}
            onClick={() => setImportOpen(true)}
            title={
              uploadTarget === 'store' ? `Upload ${selectedStore!.name}'s report`
              : uploadTarget === 'group' ? `Upload a group-wise file for ${group}`
              : 'Select a store, or a group for a combined file'
            }
          >
            <span className="inline-flex items-center gap-1"><Upload size={12} /> {uploadTarget === 'group' ? 'Upload (Group)' : 'Upload Report'}</span>
          </Button>
        </div>
      </FilterBar>

      <Table<SisSaleRow>
        storageKey="sis-sales"
        paginated
        title="Sales"
        count={`${rows.length} lines`}
        rows={rows}
        rowKey={(r) => r.id}
        loading={query.isFetching && rows.length === 0}
        search={search}
        empty="No sales yet for this filter — upload a store's daily report to populate this."
        columns={[
          { key: 'date', header: 'Date', cell: (r) => formatDate(r.saleDate) },
          { key: 'txn', header: 'Bill No', pickerLabel: 'Bill No (Txn)', cell: (r) => <span className="font-extrabold text-accent">{r.txnId}</span> },
          { key: 'store', header: 'Store', cell: (r) => <span className="font-bold">{`${r.storeName}${r.storeCity ? ` (${r.storeCity})` : ''}`}</span> },
          { key: 'group', header: 'Group', cell: (r) => r.storeGroup || '—' },
          { key: 'item', header: 'Item', cell: (r) => `${r.itemCode} · ${r.itemName}` },
          { key: 'barcode', header: 'Barcode', cell: (r) => r.itemBarcode || '—' },
          { key: 'srcinv', header: 'Src Invoice', pickerLabel: 'Source Invoice (FIFO)', cell: (r) => r.srcInvoice || '—' },
          {
            key: 'qty',
            header: 'Qty',
            align: 'right',
            cell: (r) =>
              r.returnFromSis ? (
                <Badge tone="danger">⇐ SIS {r.qty}</Badge>
              ) : r.isReturn ? (
                <Badge tone="warning">↩ {r.qty}</Badge>
              ) : (
                <strong>{r.qty}</strong>
              ),
          },
          { key: 'mrp', header: 'MRP', align: 'right', cell: (r) => `₹${formatINR(r.mrp)}` },
          { key: 'disc', header: 'Disc', align: 'right', cell: (r) => (r.discount ? `₹${formatINR(r.discount)}` : '—') },
          {
            key: 'discpct',
            header: 'Disc %',
            align: 'right',
            // % off the pre-discount amount (charged gross + discount).
            cell: (r) => {
              const preDisc = Math.abs(r.netValue) + r.discount;
              return r.discount > 0 && preDisc > 0 ? `${((r.discount / preDisc) * 100).toFixed(1)}%` : '—';
            },
          },
          { key: 'gross', header: 'Gross Amt', align: 'right', cell: (r) => <strong>₹{formatINR(r.netValue)}</strong> },
          { key: 'tax', header: 'Tax Amt', align: 'right', cell: (r) => (r.taxAmount ? `₹${formatINR(signedTax(r))}` : '—') },
          { key: 'netamt', header: 'Net Amt', align: 'right', cell: (r) => <strong className="text-accent">₹{formatINR(r.netValue - signedTax(r))}</strong> },
          { key: 'batch', header: 'Batch', cell: (r) => r.batchNo },
        ]}
      />

      {importOpen && uploadTarget && (
        <ExcelImportModal
          open={importOpen}
          onClose={() => setImportOpen(false)}
          title={uploadTarget === 'group' ? `Group Sales Report — ${group}` : `Sales Report — ${selectedStore!.name}`}
          expectedColumns={[
            ...(uploadTarget === 'group' ? ['Store'] : []),
            'Bill No', 'Barcode', 'Invoice No', 'Qty', 'MRP', 'Disc', 'Gross Amt', 'Tax Amt', 'Return (Y/N)', 'Return from SIS (Y/N)', 'Sale Date',
          ]}
          columnDetails={[
            ...(uploadTarget === 'group' ? [{ name: 'Store', hint: "Store's report label (set via the Labels button)", example: 'STORE-LABEL' }] : []),
            { name: 'Bill No', hint: 'Store sale bill / transaction id (unique per item line)', example: 'TXN-1001' },
            { name: 'Barcode', hint: 'Item barcode — identifies the item; its name & code come from the Item Master', example: 'GH002676' },
            { name: 'Invoice No', hint: "Store's own sale invoice no", optional: true },
            { name: 'Qty', hint: 'Units sold', example: '3' },
            { name: 'MRP', hint: 'Per-unit MRP (₹)', example: '2000' },
            { name: 'Disc', hint: 'Discount amount on the line (₹)', example: '600', optional: true },
            { name: 'Gross Amt', hint: 'Amount actually charged — incl GST, net of discount (₹)', example: '5400' },
            { name: 'Tax Amt', hint: 'GST amount within the line (₹)', example: '257.14', optional: true },
            { name: 'Return (Y/N)', hint: 'Consumer return to the store — Y adds it back to the store', example: 'N', optional: true },
            { name: 'Return from SIS (Y/N)', hint: 'Store returns UNSOLD stock to GRHAMOY — Y reduces net sold & store on-hand (not commission/CN); Invoice No = the dispatch invoice', example: 'N', optional: true },
            { name: 'Sale Date', hint: 'Date of sale (YYYY-MM-DD)', example: '2026-08-14' },
          ]}
          onImport={(file, progress) => {
            const onProgress = (done: number, total: number) => progress(`Saving… ${done} of ${total} rows`);
            return uploadSold((allow) =>
              uploadTarget === 'group'
                ? importSisGroupReport(group, file, 'sold', allow, onProgress)
                : importSisSalesReport(selectedStore!.id, file, 'sold', allow, onProgress));
          }}
          onSuccess={() => {
            qc.invalidateQueries({ queryKey: ['sis-sales'] });
            qc.invalidateQueries({ queryKey: ['sis-stock'] });
            qc.invalidateQueries({ queryKey: ['sis-ledger'] });
            qc.invalidateQueries({ queryKey: ['sis-exceptions'] });
          }}
        />
      )}

      <StoreLabelsModal open={labelsOpen} onClose={() => setLabelsOpen(false)} group={group || undefined} />
      <SalesUploadsModal
        open={uploadsOpen}
        onClose={() => setUploadsOpen(false)}
        filter={{ group: group || undefined, partyId: partyId ? Number(partyId) : undefined }}
      />
    </div>
  );
}
